import type { Rent, RentInstallmentWithDelta } from '@einwert/valuation-api-client'
import type { TenantRentalStatusLabelKeys, TenantUsageTypeLabelKeys } from 'ComponentLibrary/src/common/tenant'
import type { TenantDetailsTableCopy } from 'ComponentLibrary/src/widgets/TenantTables/TenantDetailsTable/TenantDetailsTable'
import type {
    CustomerExistingExternalValuation as ExistingExternalValuation,
    CustomerIncomeTenantParameter as IncomeValuationTenantParameterSet,
    CustomerTenant as TenantV3,
} from '@einwert/valuation-api-client'
import type { TFunction } from 'i18next'
import { keyBy } from 'lodash'

const tenantUsageTypeKeys = [
    'residential',
    'commercial',
    'gastronomy',
    'industry',
    'hotel',
    'storage',
    'underground_parking',
    'leisure',
    'social',
    'archive',
    'outdoor_parking',
    'parking',
    'retail',
    'logistics',
    'office',
    'other',
    'total',
    'other_parking',
    'open_space',
    'other_use',
    'garage_parking',
] as const satisfies TenantUsageTypeLabelKeys[]

const tenantRentalStatusKeys = ['rented', 'unrented', 'self_used'] as const satisfies TenantRentalStatusLabelKeys[]

export type ExternalValuationTenantRow = TenantV3 & {
    tenantIndex: number
    tenantParameters?: IncomeValuationTenantParameterSet
    market_rent?: Rent
    market_rent_details?: RentInstallmentWithDelta
}

export const mapEconomicUnitTenantRows = (
    tenants: TenantV3[],
    tenantParameters: Record<string, IncomeValuationTenantParameterSet>
): ExternalValuationTenantRow[] =>
    tenants.map((tenant, tenantIndex) => ({
        ...tenant,
        tenantParameters: tenantParameters[tenant.uuid],
        market_rent: tenantParameters[tenant.uuid]?.reasonable_rent,
        market_rent_details: tenantParameters[tenant.uuid]?.reasonable_rent_details,
        tenantIndex,
    }))

export function mapExternalValuationTenantRows(valuation: ExistingExternalValuation): ExternalValuationTenantRow[] {
    const economicUnit = valuation.economic_units[0]
    const tenantParametersById = keyBy(economicUnit.income_valuation_parameters.tenant_parameters, 'tenant_uuid')

    return mapEconomicUnitTenantRows(economicUnit.tenantsV3 ?? [], tenantParametersById)
}

const getTenantUsageTypeLabels = (tCommon: TFunction): Record<TenantUsageTypeLabelKeys, string> =>
    Object.fromEntries(tenantUsageTypeKeys.map((key) => [key, tCommon(`tenantUsageTypes.${key}`)])) as Record<
        TenantUsageTypeLabelKeys,
        string
    >

const getTenantRentalStatusLabels = (tTenantRentalStatuses: TFunction): Record<TenantRentalStatusLabelKeys, string> =>
    Object.fromEntries(tenantRentalStatusKeys.map((key) => [key, tTenantRentalStatuses(key)])) as Record<
        TenantRentalStatusLabelKeys,
        string
    >

export const getTenantDetailsTableCopy = (
    tCommon: TFunction,
    tTenantRentalStatuses: TFunction,
    tTenantDetailsTable: TFunction
): TenantDetailsTableCopy => ({
    discrepancyTooltip: {
        title: tTenantDetailsTable('discrepancyTooltip.title'),
        description: tTenantDetailsTable('discrepancyTooltip.description'),
        details: tTenantDetailsTable('discrepancyTooltip.details'),
        summary: tTenantDetailsTable('discrepancyTooltip.summary'),
        discrepancy: tTenantDetailsTable('discrepancyTooltip.discrepancy'),
    },
    discrepancyTooltipTitle: tTenantDetailsTable('discrepancyTooltipTitle'),
    discrepancyHeaders: {
        units: tTenantDetailsTable('discrepancyHeaders.units'),
        area: tTenantDetailsTable('discrepancyHeaders.area'),
        contractRentPerYear: tTenantDetailsTable('discrepancyHeaders.contractRentPerYear'),
        marketRentPerYear: tTenantDetailsTable('discrepancyHeaders.marketRentPerYear'),
    },
    headers: {
        id: tTenantDetailsTable('headers.id'),
        floor: tTenantDetailsTable('headers.floor'),
        tenant: tTenantDetailsTable('headers.tenant'),
        usageType: tCommon('usageType'),
        structure: tCommon('structure'),
        subUsageType: tTenantDetailsTable('headers.subUsageType'),
        leaseId: tTenantDetailsTable('headers.leaseId'),
        status: tTenantDetailsTable('headers.status'),
        area: tTenantDetailsTable('headers.area'),
        units: tTenantDetailsTable('headers.units'),
        startDate: tTenantDetailsTable('headers.startDate'),
        endDate: tTenantDetailsTable('headers.endDate'),
        remainingLeaseTerm: tTenantDetailsTable('headers.remainingLeaseTerm'),
        options: tCommon('options'),
        contractRentPerMonth: tTenantDetailsTable('headers.contractRentPerMonth'),
        contractRentPerYear: tTenantDetailsTable('headers.contractRentPerYear'),
        marketRentPerMonth: tTenantDetailsTable('headers.marketRentPerMonth'),
        marketRentPerYear: tTenantDetailsTable('headers.marketRentPerYear'),
        absoluteDelta: tTenantDetailsTable('headers.absoluteDelta'),
        deltaPercentage: tTenantDetailsTable('headers.deltaPercentage'),
        comments: tTenantDetailsTable('headers.comments'),
    },
    labels: {
        area: tTenantDetailsTable('labels.area'),
        vacancyArea: tTenantDetailsTable('labels.vacancyArea'),
        units: tTenantDetailsTable('labels.units'),
        vacancyUnits: tTenantDetailsTable('labels.vacancyUnits'),
        contractRent: tCommon('contractRent'),
        marketRent: tCommon('marketRent'),
        indefinite: tTenantDetailsTable('labels.indefinite'),
        years: tTenantDetailsTable('labels.years'),
    },
    usageTypeLabels: getTenantUsageTypeLabels(tCommon),
    rentalStatusLabels: getTenantRentalStatusLabels(tTenantRentalStatuses),
    unitLabels: {
        regular: tTenantDetailsTable('unitLabels.regular'),
        parking: tTenantDetailsTable('unitLabels.parking'),
        perYear: tTenantDetailsTable('unitLabels.perYear'),
        perSquareMeter: tTenantDetailsTable('unitLabels.perSquareMeter'),
        perUnit: tTenantDetailsTable('unitLabels.perUnit'),
        perParkingSpace: tTenantDetailsTable('unitLabels.perParkingSpace'),
    },
})
